import { keyBy } from 'lodash';
import { QuasarTableColumnList, TableSettings } from 'SharedDir/declarations/shared';
import { useSettingsStore } from 'SharedDir/stores/settings.store';
import { computed, MaybeRef, ref, toValue, watch } from 'vue';

/**
 * Набор инструментов для работы с настройками таблиц,
 * Включая сохранение и востановление настроек из хранилища на устройстве пользователя
 * @param {MaybeRef<QuasarTableColumnList<T>>} columns
 * @param {string} storeKey - ключ, под которым сохранять настройки в хранилище, если пусто, то настройки не сохраняются
 * @returns {{tableSettings: Ref<UnwrapRef<TableSettings>>, resetSettings: () => void, makeDefaultSettings: (list: MaybeRef<QuasarTableColumnList<T>>) => {hiddenColumns: any[], columnsOrder: undefined[]}, hasSavedSettings: ComputedRef<boolean>, configuredColumns: ComputedRef<Array<QuasarTableColumn<T>> | QuasarTableColumn<T>[]>}}
 */
export function useTableSettings<T extends object>(
  columns: MaybeRef<QuasarTableColumnList<T>>,
  storeKey?: string
) {
  const store = useSettingsStore();
  function makeDefaultSettings(list: MaybeRef<QuasarTableColumnList<T>>): TableSettings {
    return {
      hiddenColumns: toValue(list)
        .filter((item) => item?.hiddenDefault)
        .map(({ name }) => name),
      columnsOrder: toValue(list).map(({ name }) => name),
    };
  }
  let initialSettings: TableSettings;

  if (storeKey) {
    initialSettings = <TableSettings>store.settings[storeKey] ?? makeDefaultSettings(columns);
  } else {
    initialSettings = makeDefaultSettings(columns);
  }

  const tableSettings = ref<TableSettings>(initialSettings);

  /**
   * Флаг, указывающий, что настройки под данным ключом есть в хранилище
   * @type {ComputedRef<boolean>}
   */
  const hasSavedSettings = computed(() => {
    if (!storeKey) {
      return false;
    }

    return typeof store.settings[storeKey] !== 'undefined';
  });

  /**
   * Массив колонок для квазаровской таблицы, модифицированный в соответствии с настройками
   * @type {ComputedRef<Array<QuasarTableColumn<T>> | QuasarTableColumn<T>[]>}
   */
  const configuredColumns = computed(() => {
    const columnsValue = toValue(columns);
    const settingValue = toValue(tableSettings);
    if (
      !settingValue ||
      !Array.isArray(settingValue.columnsOrder) ||
      !Array.isArray(settingValue.hiddenColumns)
    ) {
      /**
       * Если настроек нет, или оне не корректнын то ниче не делаем, мало ли что.
       */
      return columnsValue;
    }
    const dict = keyBy(columnsValue, 'name');
    const keys =
      settingValue.columnsOrder.length === columnsValue.length
        ? settingValue.columnsOrder
        : Object.keys(dict);

    return keys
      .filter(
        (key) => !settingValue.hiddenColumns.includes(key) && typeof dict[key] !== 'undefined'
      )
      .map((key) => dict[key]);
  });

  if (storeKey) {
    watch(
      () => toValue(tableSettings),
      (newValue) => store.save(storeKey, newValue)
    );
  }

  function resetSettings() {
    tableSettings.value = makeDefaultSettings(columns);
  }

  return { configuredColumns, tableSettings, makeDefaultSettings, resetSettings, hasSavedSettings };
}
